import { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import palette from '@lib/core/src/theme/palette';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { CalendarMeetingDto } from '@/api/generated';
import { CalendarEventWithSchedule } from '@/components/CalendarEvents/types';
import { toMeeting } from '@/enums/RoutesMap';
import useMeetings from '@/hooks/useMeetings';
import { unixTimestampToTime } from '@/utils/dateUtil';

import {
  EventContainer,
  EventStripe,
  EventTime,
  EventTitle,
  PrepareButton,
  ViewButton
} from './CalendarEventStyles';
import InviteBotButton from './InviteBotButton';

dayjs.extend(utc);

interface CalendarEventProps {
  event: CalendarEventWithSchedule;
  onClose?: () => void;
  meeting?: CalendarMeetingDto;
  isLoadingInviteAll: boolean;
  scheduleBot: (event: CalendarEventWithSchedule) => Promise<void>;
  removeBot: (event: CalendarEventWithSchedule) => Promise<void>;
  setEventListWithSchedule: React.Dispatch<React.SetStateAction<CalendarEventWithSchedule[]>>;
}

const CalendarEvent = ({ event, onClose, meeting, scheduleBot, removeBot }: CalendarEventProps) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [botLoading, setBotLoading] = useState(false);

  const passed = event?.when?.endTime ? dayjs().isAfter(dayjs.unix(event.when.endTime)) : false;
  const started = event?.when?.startTime
    ? dayjs().isAfter(dayjs.unix(event.when.startTime))
    : false;
  const isRecurring = !!event?.masterEventId;
  const eventWithConference = !!event.conferencing?.url;
  const { lastMeeting, lastMeetingLoading } = useMeetings(
    undefined,
    undefined,
    undefined,
    undefined,
    !passed ? event?.masterEventId : undefined
  );

  const showPrepareButton = hovered && !passed && isRecurring && !lastMeetingLoading && lastMeeting;

  const isButtonActive = useMemo(() => event.isScheduled, [event.isScheduled]);

  const openPrepare = useCallback(() => {
    if (lastMeeting) {
      navigate(toMeeting(lastMeeting.id));
    }
    onClose?.();
  }, [navigate, onClose, lastMeeting]);

  const openMeeting = useCallback(() => {
    if (meeting) {
      navigate(toMeeting(meeting.id));
    }
  }, [navigate, meeting]);

  const handleInviteBot = useCallback(async () => {
    if (botLoading) return;
    setBotLoading(true);
    if (isButtonActive) {
      await removeBot(event);
    } else {
      await scheduleBot(event);
    }
    setBotLoading(false);
  }, [isButtonActive, removeBot, scheduleBot, event, botLoading]);

  const isFiveMinutesBefore = event.when?.startTime
    ? dayjs().isAfter(dayjs.unix(event.when.startTime).subtract(5, 'minutes'))
    : false;

  return (
    <EventContainer
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      passed={passed}
    >
      <Stack direction="row" alignItems="center" gap="13px">
        <EventStripe
          sx={{
            backgroundColor: passed
              ? hovered
                ? palette.grey[400]
                : palette.grey[600]
              : hovered
                ? palette.black
                : palette.grey[400]
          }}
          orientation="vertical"
          flexItem
        />
        <Stack gap="2px" alignItems="flex-start">
          <EventTitle color={hovered ? palette.black : palette.common.label}>
            {event.title}
          </EventTitle>
          <Stack direction="row" alignItems="center" gap="8px">
            <EventTime color={hovered ? palette.common.label : palette.title.grey}>
              {unixTimestampToTime(event?.when?.startTime)} -{' '}
              {unixTimestampToTime(event?.when?.endTime)}
            </EventTime>
            {event.isInviteable && !started && !isFiveMinutesBefore && (
              <InviteBotButton isActive={isButtonActive} inviteBot={handleInviteBot} />
            )}
          </Stack>
        </Stack>
      </Stack>

      {hovered &&
      eventWithConference &&
      meeting?.transcript &&
      (passed || meeting?.overview?.brief) ? (
        <ViewButton onClick={openMeeting}>View</ViewButton>
      ) : (
        showPrepareButton && <PrepareButton onClick={openPrepare}>Prepare</PrepareButton>
      )}
    </EventContainer>
  );
};

export default CalendarEvent;
